import examApis from '../../../apis/exams';
import marksAnalyticsApis from '../../../apis/examReports';
import { downloadReport } from '../../../utils/reportUtils';
import {
  DownloadConsolidatedReportArgs,
  DownloadSelectedExamReportsArgs,
  ExamOption,
  GenericReportRow
} from '../types/MarksAnalyticsTypes';
import { SnackbarSeverityType } from '../../../enums';

const mapExamApiResponse = (response: any): ExamOption[] => {
  const list = response;
  return list.map((exam: any) => ({
    id: exam.id,
    name: exam.name,
    termId: exam.termId
  }));
};

const deriveUniqueTermIds = (exams: ExamOption[]): string[] => {
  return Array.from(
    new Set(exams.map((exam) => exam.termId).filter(Boolean))
  ) as string[];
};

const filterInvalidExamSelections = (
  current: string[],
  options: ExamOption[]
): string[] => current.filter((id) => options.some((exam) => exam.id === id));

const buildExcelFromGenericRows = (
  rows: GenericReportRow[],
  meta: {
    school: string;
    grade: string;
    division: string;
    academicYear: string;
    preferredFileName?: string;
  }
) => {
  if (!rows.length) return;
  const fileBase =
    meta.preferredFileName ||
    `${meta.school}-MarksAnalytics-${meta.grade}-${meta.division}-${meta.academicYear}`;
  downloadReport(rows as any[], fileBase, 'Marks Analytics', () => {});
};

const getTermIds = async (
  grade: string,
  division: string,
  academicYear: string,
  school: string
): Promise<string[]> => {
  try {
    const terms: any = await examApis.getTermsFor(
      grade,
      division,
      academicYear,
      school
    );
    return terms.map((term: any) => term.id).filter(Boolean);
  } catch {
    return [];
  }
};

const getExamsForTerms = async (
  grade: string,
  division: string,
  academicYear: string,
  school: string,
  termIds: string[]
): Promise<ExamOption[]> => {
  try {
    const examsPerTerm = await Promise.all(
      termIds.map(async (termId: string) => {
        try {
          const examsResponse: any = await examApis.getExamsFor(
            grade,
            division,
            academicYear,
            school,
            termId
          );
          return examsResponse?.data ?? examsResponse ?? [];
        } catch {
          return [];
        }
      })
    );
    const allExams = examsPerTerm.flat();
    return mapExamApiResponse(allExams);
  } catch {
    return [];
  }
};

const fetchExams = async (
  grade: string,
  division: string,
  academicYear: string,
  school: string
): Promise<ExamOption[]> => {
  try {
    const termIds = await getTermIds(grade, division, academicYear, school);
    if (!termIds.length) return [];

    const exams = await getExamsForTerms(
      grade,
      division,
      academicYear,
      school,
      termIds
    );
    return exams;
  } catch {
    return [];
  }
};

const fetchReport = async (params: any) => {
  return marksAnalyticsApis.getConsolidatedMarksReport(params);
};

const getOptionLabel = (option: ExamOption) => option.name;
const isOptionEqualToValue = (option: ExamOption, value: ExamOption) =>
  option.id === value.id;

const downloadConsolidatedReport = async ({
  selectedSchool,
  academicYear,
  grade,
  division,
  fetchReport,
  buildExcelFromGenericRows,
  showToast
}: DownloadConsolidatedReportArgs): Promise<void> => {
  const response: any = await fetchReport({
    school: selectedSchool,
    academicYear,
    grade,
    division,
    examIds: undefined,
    isDetailedReport: false
  });

  if (!response.length) {
    showToast('No report found', SnackbarSeverityType.error);
    return;
  }

  const fileName = response?.fileName as string | undefined;
  buildExcelFromGenericRows(response as GenericReportRow[], {
    school: selectedSchool,
    grade,
    division,
    academicYear,
    preferredFileName: fileName
  });

  showToast('Report downloaded', SnackbarSeverityType.success);
};

const downloadSelectedExamReports = async ({
  selectedSchool,
  academicYear,
  grade,
  division,
  examsOptions,
  selectedExamIds,
  downloadMultipleExamReports,
  showToast
}: DownloadSelectedExamReportsArgs): Promise<void> => {
  const { successCount, failureCount } = await downloadMultipleExamReports({
    school: selectedSchool,
    academicYear,
    grade,
    division,
    examsOptions,
    selectedExamIds
  });

  const statusMessages: string[] = [];
  if (successCount) statusMessages.push(`${successCount} downloaded`);
  if (failureCount) statusMessages.push(`${failureCount} failed`);

  const summary = statusMessages.length
    ? statusMessages.join(', ')
    : 'No reports processed';

  const severity =
    successCount && !failureCount
      ? SnackbarSeverityType.success
      : SnackbarSeverityType.error;

  showToast(`Exam report downloads: ${summary}`, severity);
};

export {
  getOptionLabel,
  isOptionEqualToValue,
  fetchReport,
  mapExamApiResponse,
  deriveUniqueTermIds,
  filterInvalidExamSelections,
  buildExcelFromGenericRows,
  fetchExams,
  downloadConsolidatedReport,
  downloadSelectedExamReports
};
